"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DealOfferTermSchema = exports.OfferPromoSchema = exports.OFFER_COVER_MODES = exports.OFFER_TARGETS = void 0;
exports.offerIsPriced = offerIsPriced;
exports.offerShowsOneTime = offerShowsOneTime;
exports.offerAmounts = offerAmounts;
exports.offerPhasedLine = offerPhasedLine;
exports.offerFromPackageLegacy = offerFromPackageLegacy;
exports.offerFromDeviceLegacy = offerFromDeviceLegacy;
exports.resolveOffer = resolveOffer;
const zod_1 = require("zod");
const planAssignment_1 = require("./planAssignment");
/**
 * A DEAL OFFER — everything a deal changes about ONE billable line, in one
 * object, whether that line is a software package or a device finance option.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE SHAPE
 * ---------------------------------------------------------------------------
 *
 * Software and hardware grew separate models: packages carried a permanent
 * `packageDiscounts` and a temporary `packageIntros` in two different places,
 * while devices carried `deviceTerms`. Three shapes for one idea — "what does
 * this line cost, and for how long" — which meant three UIs, three validators,
 * and a capability matrix full of holes: a device could not be promoted for a
 * limited period, a package could not be discounted by a flat amount off its
 * up-front charge, and neither could express a percentage promotion.
 *
 * This is the single shape. It answers four questions and nothing else:
 *
 *   1. WHAT does it settle at?      `discount` off the list price
 *   2. WHERE does that come off?    `takenFrom` — the monthly or the one-time
 *   3. IS there an opening period?  `promo`, with its own price and length
 *   4. HOW MANY UNITS does it cover? `covers` / `coveredUnits`
 *
 * A package is a line with a quantity of one and no up-front amount, so
 * questions 2 and 4 simply have one sensible answer for it. Nothing about the
 * shape is device-specific; that is the point.
 *
 * ---------------------------------------------------------------------------
 * SETTLED vs PROMOTIONAL — two layers, deliberately
 * ---------------------------------------------------------------------------
 *
 * `discount` is PERMANENT: it decides the price the subscription settles at,
 * and it is what `priceCents` and `dealPackagesTotals` are computed from.
 * `promo` is TEMPORARY: it overrides that price for `promo.months`, after
 * which the line reverts to the settled price — not to list.
 *
 * Both can be set, because "$5/month for a year, then $79 (normally $99)" is an
 * ordinary offer and needs both layers. Collapsing them into one field would
 * make it inexpressible; keeping them in two OBJECTS is what forced an operator
 * to visit two places. One object, two layers, one control group.
 *
 * `promo.fixedPriceCents` is an ABSOLUTE price, not a further reduction. An
 * operator naming what the customer pays during the promotion is stating one
 * number; expressing it as a discount-off-a-discount is how two people arrive
 * at two different numbers.
 */
exports.OFFER_TARGETS = ['monthly', 'one_time'];
exports.OFFER_COVER_MODES = ['each', 'first_n'];
/** The opening period: a different price for a fixed number of months. */
exports.OfferPromoSchema = zod_1.z.object({
    /** A reduction off the SETTLED price. Mutually exclusive with `fixedPriceCents`. */
    discount: planAssignment_1.DiscountSchema.optional(),
    /** An absolute price during the promotion. `0` is real: free. */
    fixedPriceCents: zod_1.z.number().int().min(0).optional(),
    /** How many billing cycles it runs for. */
    months: zod_1.z.number().int().min(1),
}).refine(
// Both would be two different answers to "what do they pay", and nothing
// could choose between them without inventing a precedence rule.
(p) => !(p.discount && typeof p.fixedPriceCents === 'number'), { message: 'a promotion is either a discount or a fixed price, never both', path: ['fixedPriceCents'] }).refine((p) => Boolean(p.discount) || typeof p.fixedPriceCents === 'number', { message: 'a promotion needs either a discount or a fixed price', path: ['fixedPriceCents'] });
exports.DealOfferTermSchema = zod_1.z.object({
    /** PERMANENT. What the line settles at, as a reduction off its list price. */
    discount: planAssignment_1.DiscountSchema.optional(),
    /**
     * Which amount everything here comes off. Default `'monthly'`.
     *
     * A line with no monthly amount (an outright device) is corrected to
     * `'one_time'` at pricing time rather than trusted — see `offerAmounts`.
     */
    takenFrom: zod_1.z.enum(exports.OFFER_TARGETS).optional(),
    /** TEMPORARY. The opening period, if there is one. */
    promo: exports.OfferPromoSchema.optional(),
    /** How many units the offer covers. Default `'each'`. Per location. */
    covers: zod_1.z.enum(exports.OFFER_COVER_MODES).optional(),
    coveredUnits: zod_1.z.number().int().min(1).optional(),
    /**
     * HOW MANY LOCATIONS THE OFFER REACHES. Absent means every one.
     *
     * `covers`/`coveredUnits` answer "how many UNITS at a location"; this answers
     * "at how many LOCATIONS at all". They are different questions — "the first
     * two terminals at each of your first two stores" needs both — and collapsing
     * them would make that inexpressible.
     *
     * Ordered exactly as `freeLocationQuota` is: `index < coveredLocations` over
     * the draft's locations. Reusing the one ordering the system already has
     * means the two rules can never disagree about which location came first.
     */
    coveredLocations: zod_1.z.number().int().min(1).optional(),
    /**
     * The offer's own up-front amount, replacing whatever the catalog asks.
     * `0` is MEANINGFUL — it is the "$0 down" a deal exists to advertise.
     */
    downPaymentCents: zod_1.z.number().int().min(0).optional(),
    /**
     * Whether the one-time amount appears on the CUSTOMER-facing preview.
     * PRESENTATION ONLY: a hidden amount is still billed and still invoiced,
     * and every total in this file includes it.
     */
    showOneTimeInPreview: zod_1.z.boolean().optional(),
    /** INTERNAL negotiation notes. Never reaches a customer-facing surface. */
    reason: zod_1.z.string().optional(),
}).refine((t) => t.covers !== 'first_n' || typeof t.coveredUnits === 'number', { message: 'coveredUnits is required when covers is "first_n"', path: ['coveredUnits'] });
/** True when a term actually changes a price. An empty term is a drawer opened and closed. */
function offerIsPriced(term) {
    if (!term)
        return false;
    return Boolean(term.discount) || Boolean(term.promo) || typeof term.downPaymentCents === 'number';
}
/** Whether the one-time amount may be shown. Absent means yes — hiding money is the explicit choice. */
function offerShowsOneTime(term) {
    return term?.showOneTimeInPreview !== false;
}
/**
 * WHAT ONE LINE COSTS under an offer — the whole arithmetic, in one place.
 *
 * Pure and total. Both the browser and the server call this and nothing else,
 * which is what keeps "the number quoted is the number charged" structural.
 *
 * Rounding happens exactly once per amount, inside `applyDiscount`, on the
 * PER-UNIT figure — never on a multiplied total that is divided back out.
 *
 * The settled price is computed FIRST, and the promotion is applied to that
 * rather than to list: a promotion reverts to the discounted price, not to the
 * full one, which is what "then $79" means when list is $99.
 */
function offerAmounts(list, term) {
    const listMonthlyCents = Math.max(0, Math.round(list?.monthlyCents || 0));
    const listOneTimeCents = Math.max(0, Math.round(list?.oneTimeCents || 0));
    const base = {
        promoMonthlyCents: listMonthlyCents,
        promoOneTimeCents: listOneTimeCents,
        settledMonthlyCents: listMonthlyCents,
        settledOneTimeCents: listOneTimeCents,
        listMonthlyCents,
        listOneTimeCents,
        priced: false,
    };
    if (!term)
        return base;
    // The offer's own up-front amount replaces the catalog's BEFORE anything is
    // discounted, and `0` replaces it just as firmly as `99` does — "$0 down" is
    // the offer, not a missing value. `??`, never `||`.
    const baseOneTime = typeof term.downPaymentCents === 'number'
        ? Math.max(0, Math.round(term.downPaymentCents))
        : listOneTimeCents;
    // A discount aimed at a monthly amount that does not exist would be applied
    // to 0 and SILENTLY VANISH — the outright-device case. Corrected here so it
    // holds for every caller and for every term already in storage.
    const requested = term.takenFrom || 'monthly';
    const takenFrom = requested === 'monthly' && listMonthlyCents === 0 ? 'one_time' : requested;
    const settledMonthlyCents = takenFrom === 'monthly'
        ? (0, planAssignment_1.applyDiscount)(listMonthlyCents, term.discount)
        : listMonthlyCents;
    const settledOneTimeCents = takenFrom === 'one_time'
        ? (0, planAssignment_1.applyDiscount)(baseOneTime, term.discount)
        : baseOneTime;
    // The promotion applies to the SETTLED price, not to list.
    let promoMonthlyCents = settledMonthlyCents;
    let promoOneTimeCents = settledOneTimeCents;
    if (term.promo) {
        const target = takenFrom;
        const apply = (settled) => (typeof term.promo.fixedPriceCents === 'number'
            ? Math.max(0, Math.round(term.promo.fixedPriceCents))
            : (0, planAssignment_1.applyDiscount)(settled, term.promo.discount));
        if (target === 'monthly')
            promoMonthlyCents = apply(settledMonthlyCents);
        else
            promoOneTimeCents = apply(settledOneTimeCents);
    }
    return {
        promoMonthlyCents,
        promoOneTimeCents,
        settledMonthlyCents,
        settledOneTimeCents,
        listMonthlyCents,
        listOneTimeCents,
        ...(term.promo ? { promoMonths: term.promo.months } : {}),
        priced: offerIsPriced(term),
    };
}
/**
 * One offer as a `PhasedLine`, ready for `buildPricePhases`.
 *
 * The seam between "what an operator sold" and "what Stripe will be scheduled
 * to charge". A promotion becomes the line's `intro`; a device term becomes
 * `endsAfterMonths`. Software and hardware produce the same shape, which is
 * what lets one schedule interleave both.
 */
function offerPhasedLine(args) {
    const amounts = offerAmounts(args.list, args.term);
    return {
        key: args.key,
        quantity: args.quantity,
        standardAmountCents: amounts.settledMonthlyCents,
        ...(amounts.promoMonths && amounts.promoMonthlyCents !== amounts.settledMonthlyCents
            ? { intro: { introPriceCents: amounts.promoMonthlyCents, introMonths: amounts.promoMonths } }
            : {}),
        ...(typeof args.endsAfterMonths === 'number' ? { endsAfterMonths: args.endsAfterMonths } : {}),
    };
}
// ---------------------------------------------------------------------------
// Reading the shapes that came before
// ---------------------------------------------------------------------------
/**
 * The legacy per-package pair, as one offer.
 *
 * `packageDiscounts[id]` and `packageIntros[id]` were two fields in two places;
 * this is the same information in the unified shape. Neither legacy field is
 * removed or migrated — a deal written before this still parses, and this is
 * how every reader sees it as one thing.
 */
function offerFromPackageLegacy(args) {
    const { discount, intro } = args;
    if (!discount && !intro)
        return undefined;
    return {
        ...(discount ? { discount } : {}),
        takenFrom: 'monthly',
        ...(intro
            ? { promo: { fixedPriceCents: intro.introPriceCents, months: intro.introMonths } }
            : {}),
        ...(intro?.reason ? { reason: intro.reason } : {}),
    };
}
/**
 * A legacy `DealDeviceTerm` as an offer.
 *
 * Field-for-field: the old shape is this one minus `promo`, which is exactly
 * the capability devices did not have. Nothing is lost in translation, so a
 * deal saved before the unification prices identically after it.
 */
function offerFromDeviceLegacy(term) {
    if (!term)
        return undefined;
    return {
        ...(term.discount ? { discount: term.discount } : {}),
        ...(term.takenFrom === 'one_time' ? { takenFrom: 'one_time' } : { takenFrom: 'monthly' }),
        ...(term.covers === 'first_n'
            ? { covers: 'first_n', ...(typeof term.coveredUnits === 'number' ? { coveredUnits: term.coveredUnits } : {}) }
            : { covers: 'each' }),
        ...(typeof term.downPaymentCents === 'number' ? { downPaymentCents: term.downPaymentCents } : {}),
        ...(term.showOneTimeInPreview === false ? { showOneTimeInPreview: false } : {}),
    };
}
/**
 * THE ONE READ every consumer should use: the offer for a line, preferring the
 * unified field and falling back to whichever legacy shape the deal carries.
 *
 * A deal written after the unification has `offerTerms`; one written before has
 * `packageDiscounts`/`packageIntros`/`deviceTerms`. Both price the same way,
 * and no consumer has to know which it is looking at.
 */
function resolveOffer(args) {
    const unified = args.offerTerms?.[args.key];
    // The unified field WINS outright rather than being merged with the legacy
    // ones. A merge would have a discount an operator removed reappear from the
    // old field — the same rule `resolveDeviceSkuIds` applies for device ids.
    if (unified)
        return unified;
    const fromDevice = offerFromDeviceLegacy(args.deviceTerms?.[args.key]);
    if (fromDevice)
        return fromDevice;
    return offerFromPackageLegacy({
        discount: args.packageDiscounts?.[args.key],
        intro: args.packageIntros?.[args.key],
    });
}
//# sourceMappingURL=dealOffer.js.map